"use client";

import { motion } from "framer-motion";
import { User, Briefcase, ArrowLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense } from "react";

function AuthChoiceContent() {
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect");
  const isBookingFlow = redirect === "/booking";

  const options = [
    {
      id: "customer",
      title: "Customer Portal",
      desc: "Post jobs and manage your property maintenance",
      icon: <User size={32} className="text-emerald-600" />,
      loginUrl: `/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
      signupUrl: `/register${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
      color: "emerald"
    },
    {
      id: "contractor",
      title: "Contractor Portal",
      desc: "Find jobs and manage your professional business",
      icon: <Briefcase size={32} className="text-blue-600" />,
      loginUrl: "/contractor/login",
      signupUrl: "/contractor/onboarding",
      color: "blue"
    }
  ];

  const visibleOptions = isBookingFlow
    ? options.filter((o) => o.id === "customer")
    : options;

  return (
    <div className="w-full max-w-4xl">
      <div className="flex flex-col items-center mb-8">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-950 shadow-xl"
        >
          <span className="text-xl font-black text-white">F</span>
        </motion.div>
        
        <h1 className="text-3xl font-bold text-slate-950 tracking-tight text-center mb-2">Choose your portal</h1>
        <p className="text-slate-500 font-medium text-center max-w-md text-base">Select how you want to use Fixer today.</p>
      </div>

      <div className={`grid gap-8 ${isBookingFlow ? "max-w-md mx-auto" : "md:grid-cols-2"}`}>
        {visibleOptions.map((opt, i) => (
          <motion.div
            key={opt.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="glass rounded-[2.5rem] border border-white/40 p-8 md:p-10 shadow-2xl hover:border-white/60 transition-all flex flex-col items-center text-center group"
          >
            <div className={`h-16 w-16 rounded-2xl bg-${opt.color}-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
              {opt.icon}
            </div>
            <h2 className="text-2xl font-bold text-slate-950 mb-2">{opt.title}</h2>
            <p className="text-slate-500 font-medium mb-8 text-base leading-relaxed">{opt.desc}</p>
            
            <div className="w-full space-y-4 mt-auto">
              <Link 
                href={opt.loginUrl}
                className={`flex items-center justify-center gap-2 w-full py-4 rounded-xl bg-emerald-600 text-white font-bold text-base hover:bg-slate-800 transition-all shadow-lg active:scale-[0.98]`}
              >
                Login
                <ChevronRight size={18} />
              </Link>
              <Link 
                href={opt.signupUrl}
                className="flex items-center justify-center gap-2 w-full py-4 rounded-xl bg-white border border-slate-200 text-slate-950 font-bold text-base hover:bg-slate-50 transition-all active:scale-[0.98]"
              >
                Register
              </Link>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <Link href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-600 text-sm font-bold transition-colors">
          <ArrowLeft size={16} />
          Back to Home
        </Link>
      </div>
    </div>
  );
}

export default function AuthChoicePage() {
  return (
    <main className="min-h-screen bg-mesh flex items-center justify-center px-6 font-sans">
      <Suspense fallback={<div className="text-slate-500">Loading portals...</div>}>
        <AuthChoiceContent />
      </Suspense>
    </main>
  );
}
